import React, { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const PerformanceDashboardPage = () => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [minScore, setMinScore] = useState('');
  const [maxScore, setMaxScore] = useState('');
  const [strategy, setStrategy] = useState('standard');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const params = {};
    if (courseId) params.courseId = courseId;
    if (minScore) params.minScore = minScore;
    if (maxScore) params.maxScore = maxScore;
    if (strategy) params.strategy = strategy;
    try {
      const res = await api.get('/performance/students', { params });
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Only admins can override scores
  const onOverride = async (userId) => {
    if (user.role !== 'admin') return;
    const remark = prompt('Enter remark:');
    const finalScore = prompt('Enter final score:');
    if (finalScore) {
      await api.post(`/performance/students/${userId}/override`, { finalScore, remark });
      fetchData();
    }
  };

  const exportCSV = () => {
    const csv = [
      ['Name','User ID','Quiz Titles','Courses','Quiz Count','Final Score'],
      ...data.map(r => [r.name, r.userId, r.quizTitles || '', r.courseTitles || '', r.quizCount || 0, r.finalScore])
    ].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'performance-summary.csv';
    a.click();
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white shadow rounded-lg">
      <h1 className="text-4xl font-bold text-gray-800 mb-6">Performance Dashboard</h1>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <input
          placeholder="Course ID"
          value={courseId}
          onChange={e => setCourseId(e.target.value)}
          className="w-32 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          placeholder="Min Score"
          value={minScore}
          onChange={e => setMinScore(e.target.value)}
          className="w-24 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          placeholder="Max Score"
          value={maxScore}
          onChange={e => setMaxScore(e.target.value)}
          className="w-24 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={strategy}
          onChange={e => setStrategy(e.target.value)}
          className="w-40 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="standard">Average</option>
          <option value="highest">Highest</option>
          <option value="weighted">Weighted</option>
        </select>
        <button
          onClick={fetchData}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >Apply</button>
        <button
          onClick={exportCSV}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
        >Export CSV</button>
      </div>
      {/* Search field */}
      <input
        type="text"
        placeholder="Search students..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className="w-full max-w-sm border rounded px-3 py-2 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quiz Titles</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Final Score</th>
              {user.role === 'admin' && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data
              .filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((r, index) => (
              <tr key={`${r.userId}-${index}`}>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{r.name}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{r.userId}</td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  <div className="max-w-xs truncate" title={r.quizTitles}>
                    {r.quizTitles || '-'}
                  </div>
                  {r.quizCount && (
                    <div className="text-xs text-gray-500">({r.quizCount} quiz{r.quizCount !== 1 ? 'es' : ''})</div>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  <div className="max-w-xs truncate" title={r.courseTitles}>
                    {r.courseTitles || '-'}
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{r.finalScore ? r.finalScore.toFixed(2) : '-'}</td>
                {user.role === 'admin' && (
                  <td className="px-4 py-2 whitespace-nowrap">
                    <button
                      onClick={() => onOverride(r.userId)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
                    >Override</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceDashboardPage;
